import { Box, Grid, Tooltip, Typography } from "@mui/material"
import React, { useRef, useState } from "react"
import TransparentImageIcon from "assets/icons/TransparentImageIcon.svg"
import RedCloseIcon from "assets/icons/RedCloseIcon.svg"
import { bytesToSize, removeDecimalUpto1 } from "helpers/functions"
import { UploadFieldContainer } from "./components/FieldContainer"
import StyledLabel from "components/StyledComponents/StyledLabel"
import AppButtonGradient from "components/StyledComponents/AppButtonGradient"
import AppButton from "components/StyledComponents/AppButton"
import InfoIcon from "@mui/icons-material/Info"

const FileUploadField = ({
  label,
  isRequired,
  placeholder = "",
  error,
  touched,
  value,
  onChange = () => {},
  onDelete = () => {},
  allowedTypes = ["image/png", "image/jpeg", "image/jpg"], // Can override for other files
  maxFileSizeInBytes,
  maxFileNameLength = 200,
  aspectWidth,
  aspectHeight,
  disabled,
  isToolTip = false,
  renderToolTip,
  isVariant = false,
  ...props
}) => {
  const inputRef = useRef(null)
  const [internalError, setInternalError] = useState(false)

  const isError = !value && (internalError || (touched && error))

  const validateFile = async (file) => {
    if (!allowedTypes.includes(file.type)) {
      setInternalError(`Unsupported file type`)
      return false
    }

    if (file.name.length > maxFileNameLength) {
      setInternalError(`File name exceeds ${maxFileNameLength} characters`)
      return false
    }

    if (maxFileSizeInBytes && file.size > maxFileSizeInBytes) {
      setInternalError(`Max size ${bytesToSize(maxFileSizeInBytes)}`)
      return false
    }

    if (aspectWidth && aspectHeight && file.type.startsWith("image/")) {
      const img = new Image()
      img.src = window.URL.createObjectURL(file)

      await new Promise((resolve) => {
        img.onload = () => {
          const actualRatio = img.naturalWidth / img.naturalHeight
          const expectedRatio = aspectWidth / aspectHeight
          if (removeDecimalUpto1(actualRatio) !== removeDecimalUpto1(expectedRatio)) {
            setInternalError(`Image must be ${aspectWidth}:${aspectHeight} ratio`)
            resolve(false)
          } else {
            resolve(true)
          }
        }
        img.onerror = () => resolve(false)
      })
    }

    return true
  }

  const handleFileChange = async (e) => {
    setInternalError(false)
    const file = e.target.files?.[0]
    if (file) {
      const isValid = await validateFile(file)
      if (isValid) onChange(file)
    }
  }

  const renderFilePreview = () => {
    const fileUrl = value?.url || URL.createObjectURL(value)

    return (
      <Box className="d-flex w-100 h-100 imageDataContainer">
        <Box
          className="imageDataContainerInner flex-grow-1 flex-shrink-1"
          onClick={() => window.open(fileUrl, "_blank")}>
          <Box className="imageContainer">
            <img
              src={fileUrl || TransparentImageIcon}
              alt=""
              style={{ maxWidth: 20, maxHeight: 20 }}
            />
          </Box>
          <Box className="imageData d-flex justify-content-center">
            <Typography variant="body2Regular">
              {decodeURIComponent(value?.url?.split("/")?.pop() || value?.name)}
            </Typography>
            {value?.size && (
              <Typography variant="tag1" sx={{ color: "#ADB5BD" }}>
                {bytesToSize(value.size)}
              </Typography>
            )}
          </Box>
        </Box>

        <img
          src={RedCloseIcon}
          alt="Delete"
          role="button"
          onClick={onDelete}
          style={{ width: 20 }}
        />
      </Box>
    )
  }

  return (
    <Grid container {...props}>
      {label && (
        <StyledLabel variant="body1Regular">
          {label}
          {isToolTip && (
            <Tooltip title={renderToolTip}>
              <InfoIcon fontSize="small" className="ms-1" />
            </Tooltip>
          )}
          {isRequired && <span>*</span>}
        </StyledLabel>
      )}

      {console.log("value", value)}

      <Grid item xs={12}>
        <UploadFieldContainer
          className={isError ? "error" : disabled ? "disabled" : ""}
          onClick={() => !disabled && inputRef.current?.click()}>
          {value ? (
            renderFilePreview()
          ) : (
            <Box className="d-flex w-100 h-100 noImageDataContainer">
              <Box className="placeholder">{placeholder}</Box>
              {isVariant ? (
                <AppButton disabled={disabled}>Upload</AppButton>
              ) : (
                <AppButtonGradient disabled={disabled}>Upload</AppButtonGradient>
              )}
              <input
                ref={inputRef}
                type="file"
                accept={allowedTypes.join(",")}
                style={{ display: "none" }}
                onChange={handleFileChange}
                onClick={(e) => (e.target.value = null)}
              />
            </Box>
          )}
        </UploadFieldContainer>
      </Grid>

      {isError && (
        <Typography sx={{ color: "#A9402F", fontSize: "0.75rem", marginTop: 0.5 }}>
          {internalError || error}
        </Typography>
      )}
    </Grid>
  )
}

export default FileUploadField
